import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { mediaUrl, srcSet, ytEmbed, ytThumb } from "@/lib/api";

const LOOPS = new Set(["card-new-home", "metal", "card-kitchen", "card-lanai", "hero-aerial", "hero-bath", "hero-entry", "hero-living", "hero-framing"]);

const VER = "?v=2";

export function loopFor(src) {
  const m = /^\/media\/([\w-]+)\.jpg$/.exec(src || "");
  return m && LOOPS.has(m[1]) ? `/media/v/${m[1]}.mp4` : null;
}

export function HoverVideo({ src, active, testid }) {
  const ref = useRef(null);
  const [touch] = useState(() => window.matchMedia("(hover: none)").matches);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!touch || !ref.current) return undefined;
    const io = new IntersectionObserver(([e]) => setInView(e.intersectionRatio > 0.6), { threshold: [0, 0.6, 1] });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [touch]);
  const play = touch ? inView : active;
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (play) { v.play().catch(() => {}); } else { v.pause(); }
  }, [play]);
  return (
    <video ref={ref} muted loop playsInline preload="metadata" aria-hidden="true" data-testid={testid} data-playing={play ? "true" : "false"}
      className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${play ? "opacity-100" : "opacity-0"}`}>
      <source src={`${src.replace(/\.mp4$/, ".webm")}${VER}`} type="video/webm" />
      <source src={`${src}${VER}`} type="video/mp4" />
    </video>
  );
}

export function LoopVideo({ src, poster, className = "", testid, label }) {
  const local = src.startsWith("/media/") && src.endsWith(".mp4");
  const url = local ? `${src}${VER}` : mediaUrl(src);
  const webm = local ? `${src.replace(/\.mp4$/, ".webm")}${VER}` : null;
  return (
    <video key={url} poster={poster} autoPlay muted loop playsInline preload="auto" className={className} data-testid={testid}
      aria-label={label} aria-hidden={label ? undefined : "true"}>
      {webm && <source src={webm} type="video/webm" />}
      <source src={url} type="video/mp4" />
    </video>
  );
}

export function Img({ src, alt = "", className = "", eager = false, size = "md", ...rest }) {
  if (!src) return <div className={`bg-[color:var(--ib-sand)] ${className}`} aria-hidden="true" />;
  return (
    <img src={mediaUrl(src, size)} srcSet={srcSet(src)} sizes="(max-width: 768px) 100vw, 50vw" alt={alt}
      loading={eager ? "eager" : "lazy"} decoding="async" className={`object-cover ${className}`} {...rest} />
  );
}

export function SectionHead({ eyebrow, title, center = true, light = false, testid }) {
  return (
    <div className={center ? "text-center" : ""} data-testid={testid}>
      {eyebrow && <p className="ib-eyebrow">{eyebrow}</p>}
      <h2 className={`mt-2 font-display text-3xl uppercase sm:text-4xl lg:text-5xl ${light ? "text-white" : "text-[color:var(--ib-charcoal)]"}`}>{title}</h2>
      {center && <div className="ib-divider mt-3" aria-hidden="true"><span className="text-xs">◆</span></div>}
    </div>
  );
}

export function Lightbox({ images, index, onClose }) {
  const [i, setI] = useState(index);
  const n = images.length;
  const go = useCallback((d) => setI((x) => (x + d + n) % n), [n]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/95" role="dialog" aria-modal="true" data-testid="lightbox">
      <button onClick={onClose} className="absolute right-4 top-4 p-2 text-white" aria-label="Close" data-testid="lightbox-close"><X size={28} /></button>
      <button onClick={() => go(-1)} className="absolute left-2 p-3 text-white sm:left-6" aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft size={36} /></button>
      <img src={mediaUrl(images[i], "lg")} alt="" className="max-h-[88vh] max-w-[92vw] object-contain" />
      <button onClick={() => go(1)} className="absolute right-2 p-3 text-white sm:right-6" aria-label="Next" data-testid="lightbox-next"><ChevronRight size={36} /></button>
      <p className="absolute bottom-4 text-sm text-white/70">{i + 1} / {n}</p>
    </div>
  );
}

export function BeforeAfter({ before, after, caption }) {
  const [pos, setPos] = useState(50);
  return (
    <figure data-testid="before-after-slider">
      <div className="relative aspect-[16/10] select-none overflow-hidden bg-black">
        <img src={mediaUrl(after, "lg")} alt="After" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img src={mediaUrl(before, "lg")} alt="Before" className="h-full w-full max-w-none object-cover" style={{ width: `${10000 / Math.max(pos, 1)}%` }} />
        </div>
        <div className="absolute inset-y-0 w-0.5 bg-white" style={{ left: `${pos}%` }} />
        <span className="absolute left-3 top-3 bg-black/70 px-2 py-1 text-xs uppercase tracking-widest text-white">Before</span>
        <span className="absolute right-3 top-3 bg-[color:var(--ib-orange)] px-2 py-1 text-xs uppercase tracking-widest text-white">After</span>
        <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(+e.target.value)} aria-label="Before and after comparison"
          className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0" data-testid="before-after-range" />
      </div>
      {caption && <figcaption className="mt-2 text-sm text-black/60">{caption}</figcaption>}
    </figure>
  );
}

export function VideoCard({ video }) {
  const [play, setPlay] = useState(false);
  const embed = video.kind !== "upload" ? ytEmbed(video.url) : null;
  const poster = video.poster ? mediaUrl(video.poster, "md") : ytThumb(video.url);
  return (
    <div data-testid={`video-card-${video.id}`}>
      <div className="relative aspect-video overflow-hidden bg-black">
        {play ? (embed
          ? <iframe src={embed} title={video.title} className="h-full w-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
          : <video src={mediaUrl(video.upload || video.url)} controls autoPlay playsInline preload="none" poster={poster} className="h-full w-full" />)
          : (
            <button onClick={() => setPlay(true)} className="group absolute inset-0 h-full w-full" aria-label={`Play ${video.title}`} data-testid={`video-play-${video.id}`}>
              {poster && <img src={poster} alt="" loading="lazy" className="h-full w-full object-cover opacity-80 transition-opacity group-hover:opacity-100" />}
              <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[color:var(--ib-orange)] text-white transition-transform group-hover:scale-110"><Play size={26} /></span>
            </button>
          )}
      </div>
      <p className="mt-3 font-display text-lg uppercase">{video.title}</p>
      {video.description && <p className="text-sm text-black/60">{video.description}</p>}
    </div>
  );
}
